// Section 16: happiness moves 40% of the way to its target each season (so it lags conditions
// by about two seasons); the target is 50 + needs (+-30) + belonging (+-10) + family (+-10) +
// work (+5 skilled, -5 idle 4+ seasons) + safety (+-8) + festival (+10 for 2 seasons) + grief
// (-15), each a modifier on `happinessTarget` (needs, age, skills, policies, norms contributors).
// Trust is event-driven, changed only by what the player did. This phase applies the rows that
// follow from the season's bodies: all needs met (+2 everyone), idle adults 4+ seasons (-1; the
// retired are not idle) and the first season short of safety (Section 6: -4). The rationing
// rows are the rationing phase's (Stream A), justice the crime phase's; the rest are applied by
// the society and lifecycle phases.
import { clamp } from '../../core/math';
import { isChild, isRetired, lastCompletedReport } from '../../core/queries';
import type { Person, PhaseContext, TrustCause } from '../../core/types';
import { num } from '../shared/value';

export function updateHappiness(ctx: PhaseContext, p: Person): void {
  const target = clamp(
    num(ctx, { kind: 'happinessTarget', personId: p.id }),
    0,
    ctx.cfg.people.statMax,
  );
  const happiness = p.happiness;
  p.happiness = clamp(
    happiness + (target - happiness) * ctx.cfg.happiness.approachRate,
    0,
    ctx.cfg.people.statMax,
  );
}

export interface TrustTally {
  deltas: Map<TrustCause, { delta: number; people: number }>;
}

function applyTrust(ctx: PhaseContext, p: Person, cause: TrustCause, tally: TrustTally): void {
  // `p.id` read here only; callers pass the draft person (Section 2: proxy reads cost).
  const delta = num(ctx, { kind: 'trustChange', personId: p.id, cause });
  if (delta === 0) return;
  p.trust = clamp(p.trust + delta, 0, ctx.cfg.people.statMax);
  const row = tally.deltas.get(cause) ?? { delta: 0, people: 0 };
  row.delta += delta;
  row.people += 1;
  tally.deltas.set(cause, row);
}

/** Section 16 trust rows that follow from this season's needs and idleness. */
export function updateTrust(ctx: PhaseContext, people: readonly Person[], tally: TrustTally): void {
  const needs = ctx.state.currentReport?.needs ?? ctx.report.current.needs;
  const before = lastCompletedReport(ctx.state)?.needs ?? {};
  const short = ctx.cfg.needs.shortThreshold;
  const met = ctx.cfg.needs.metThreshold;
  const allMet = people.every((p) => {
    const n = needs[p.id];
    return (
      n === undefined || (n.food >= met && n.water >= met && n.shelter >= met && n.warmth >= met)
    );
  });
  for (const p of people) {
    // Job, age and streaks do not change in this phase: read them from the committed person
    // rather than through the draft (Section 2 budget).
    const id = p.id;
    const q = ctx.state.people[id] ?? p;
    if (allMet) applyTrust(ctx, p, 'all-needs-met', tally);
    if (
      q.job === 'rest' &&
      !isChild(ctx.cfg, q) &&
      !isRetired(ctx.state, q, ctx.cfg) &&
      q.streaks.idle >= ctx.cfg.jobs.rest.idleSeasonsBeforePenalty
    )
      applyTrust(ctx, p, 'idle', tally);
    // Section 6 safety row, "short one season": the first season of a safety shortage (D-36).
    const safety = needs[id]?.safety ?? 1;
    if (safety < short && (before[id]?.safety ?? 1) >= short)
      applyTrust(ctx, p, 'safety-short', tally);
  }
}
